/**
 * AtlasThreatIndicatorModule — a screen-edge arc toward whoever has a blow wound up at YOU (sweep 2026-10-05; the
 * client scan's F6). Complements the combat telegraphs (which show the windup ON the attacker — useless when the
 * attacker is behind you). Rules + math in lib/threat_indicator.ts.
 *
 *   SOURCES   an NPC / creature / bounty hunter's `npc_strike` row whose target is me (read straight off the table's
 *             insert/delete — WildsModule's own subscription is not touched); a PvP windup (`atlas:pvp-tell`: attacker
 *             hex + ms to impact; the attacker's position follows its replicated avatar). The hunter's windups ARE
 *             npc_strike rows; its earlier "committed / closing" state is not a blow yet and is not shown.
 *   THE ARC   at the bearing relative to the CAMERA (0 = ahead), brightening toward the impact, gone on the resolve.
 *             The pulse follows the camera-motion comfort setting (off → steady). Never in a headset, in space, on the
 *             placement map, under the loading gate or in a cinematic.
 *   Z-ORDER   32: over the world and the HUD strips, under every window (40+). THE TOKEN LAW: `--ax-*` only.
 * Lever `__threatIndicator()` (also `inject` / `clear` — a fake windup for probes; nothing is sent to the server).
 */
import type { App, IModule } from 'enginex';
import * as THREE from 'three/webgpu';
import { Phase, makeSystem } from '../engine-compat.js';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { headingOf } from '../lib/waypoint_hud.js';
import { isCinematic } from '../lib/camera_model.js';
import { cameraMotionScale } from '../lib/camera_comfort.js';
import {
  MAX_ARCS, alphaFor, gateReason, liveThreats, pulse, ringPoint, sideWords, threatBearing, urgency, type Threat, type ThreatSource,
} from '../lib/threat_indicator.js';

interface StrikeRow { attackerKey: string; targetKind: string; targetPlayer: { isEqual(o: unknown): boolean }; windupMs: number; x: number; z: number }
const MARGIN_PX = 54;

export class AtlasThreatIndicatorModule implements IModule {
  readonly name = 'AtlasThreatIndicator';
  private app: App | null = null;
  private root: HTMLDivElement | null = null;
  private readonly arcs: HTMLDivElement[] = [];
  private readonly threats = new Map<string, Threat>();
  private hooked: unknown = null;
  private offPvp: (() => void) | null = null;
  private readonly dir = new THREE.Vector3();
  private last: { shown: number; gate: string; items: Array<{ id: string; source: ThreatSource; relDeg: number; side: string; urgency: number; alpha: number; remainingMs: number }> } = { shown: 0, gate: '', items: [] };

  init(app: App): void {
    this.app = app;
    const root = document.createElement('div');
    root.id = 'threat-indicator';
    root.setAttribute('aria-hidden', 'true');
    root.style.cssText = 'position:fixed;inset:0;pointer-events:none;z-index:32;display:none;overflow:hidden';
    for (let i = 0; i < MAX_ARCS; i++) {
      const a = document.createElement('div');
      a.dataset.arc = String(i);
      // An arc: a crescent hugging the screen edge, brightest at its outer rim, pointing at the attacker.
      a.style.cssText = 'position:absolute;left:0;top:0;width:150px;height:40px;margin:-20px 0 0 -75px;display:none;will-change:transform,opacity;'
        + 'border-radius:50% 50% 0 0 / 100% 100% 0 0;'
        + 'background:radial-gradient(ellipse 100% 100% at 50% 100%, transparent 52%, color-mix(in srgb, var(--ax-bad) 85%, transparent) 76%, var(--ax-bad) 100%);'
        + 'filter:drop-shadow(0 0 8px var(--ax-bad))';
      root.appendChild(a);
      this.arcs.push(a);
    }
    document.body.appendChild(root);
    this.root = root;
    this.offPvp = bus.on('atlas:pvp-tell', (p) => this.onPvp(p));
    app.world.addSystem(makeSystem('ThreatIndicator', Phase.UI, 35, () => this.frame()));
    (window as unknown as Record<string, unknown>).__threatIndicator = () => ({
      ...this.last, count: this.threats.size,
      inject: (x: number, z: number, ms = 1500, source: ThreatSource = 'npc') => this.add({ id: `fake:${x}:${z}`, source, x, z, startMs: performance.now(), impactMs: performance.now() + ms }),
      clear: () => this.threats.clear(),
    });
  }

  private add(t: Threat): void { this.threats.set(t.id, t); }

  private onPvp(p: { attackerHex: string; itemKey: string; impactInMs: number } | null): void {
    for (const k of [...this.threats.keys()]) if (k.startsWith('pvp:')) this.threats.delete(k);
    if (!p) return;
    const foe = atlas.remotes.get(p.attackerHex);
    const now = performance.now();
    // An attacker outside the AOI mirror has no position to point at — no arc rather than a guess.
    if (!foe) return;
    this.add({ id: `pvp:${p.attackerHex}`, source: 'pvp', x: foe.targetX, z: foe.targetZ, startMs: now, impactMs: now + Math.max(0, p.impactInMs) });
  }

  /** Hook the npc_strike table once per connection. */
  private ensureHooks(): void {
    const conn = atlas.conn;
    if (!conn || this.hooked === conn) return;
    const db = conn.db as unknown as { npcStrike?: { onInsert(cb: (c: unknown, r: StrikeRow) => void): void; onDelete(cb: (c: unknown, r: StrikeRow) => void): void } };
    if (!db.npcStrike) return; // pre-publish bindings
    this.hooked = conn;
    db.npcStrike.onInsert((_c, row) => {
      if (row.targetKind !== 'player' || !conn.identity || !row.targetPlayer.isEqual(conn.identity)) return;
      const now = performance.now();
      const source: ThreatSource = row.attackerKey.startsWith('hunter') || row.attackerKey.includes('bounty') ? 'hunter' : 'npc';
      this.add({ id: `npc:${row.attackerKey}`, source, x: row.x, z: row.z, startMs: now, impactMs: now + row.windupMs });
    });
    db.npcStrike.onDelete((_c, row) => { this.threats.delete(`npc:${row.attackerKey}`); });
  }

  private cameraHeading(): number {
    let heading = -(atlas.localYaw ?? 0); // the rig's forward as a compass heading — the fallback when no camera is found
    const cam = atlas.localMesh?.children.find((c) => (c as { isCamera?: boolean }).isCamera) as THREE.Camera | undefined;
    if (cam) { cam.getWorldDirection(this.dir); if (Math.hypot(this.dir.x, this.dir.z) > 1e-4) heading = headingOf(this.dir.x, this.dir.z); }
    return heading;
  }

  private hideAll(gate: string): void {
    if (this.root && this.root.style.display !== 'none') this.root.style.display = 'none';
    this.last = { shown: 0, gate, items: [] };
  }

  private frame(): void {
    this.ensureHooks();
    const now = performance.now();
    // Prune the resolved / stale, and follow a PvP attacker's moving avatar.
    for (const [k, t] of this.threats) {
      if (liveThreats([t], now).length === 0) { this.threats.delete(k); continue; }
      if (t.source === 'pvp') { const f = atlas.remotes.get(k.slice(4)); if (f) { t.x = f.targetX; t.z = f.targetZ; } }
    }
    const gate = gateReason({
      xrPresenting: atlas.xrPresenting, spaceFlight: atlas.spaceFlight, placementView: atlas.placementView, loadingGate: atlas.loadingGate,
      cinematic: isCinematic() || document.body.classList.contains('cinematic'), inWorld: atlas.currentWorldId !== null,
    });
    const live = gate ? [] : liveThreats([...this.threats.values()], now);
    if (!this.root || live.length === 0) { this.hideAll(gate); return; }
    if (this.root.style.display !== 'block') this.root.style.display = 'block';
    const W = window.innerWidth, H = window.innerHeight;
    const heading = this.cameraHeading();
    const motion = cameraMotionScale(false);
    const items: typeof this.last.items = [];
    this.arcs.forEach((el, i) => {
      const t = live[i];
      if (!t) { if (el.style.display !== 'none') el.style.display = 'none'; return; }
      const rel = threatBearing(heading, atlas.localX, atlas.localZ, t);
      const u = urgency(now, t.startMs, t.impactMs);
      const alpha = alphaFor(u) * pulse(now, u, motion);
      const p = ringPoint(rel, W, H, MARGIN_PX);
      el.style.display = 'block';
      el.style.opacity = alpha.toFixed(3);
      el.style.transform = `translate(${p.x.toFixed(1)}px,${p.y.toFixed(1)}px) rotate(${p.deg.toFixed(1)}deg) scale(${(0.85 + 0.35 * u).toFixed(3)})`;
      items.push({ id: t.id, source: t.source, relDeg: (rel * 180) / Math.PI, side: sideWords(rel), urgency: u, alpha, remainingMs: Math.max(0, t.impactMs - now) });
    });
    this.last = { shown: items.length, gate: '', items };
  }

  dispose(): void { this.offPvp?.(); this.offPvp = null; this.root?.remove(); this.root = null; }
}
